import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';

export interface CashierCountItem {
  id: string;
  name: string;
  sort_order: number;
  product_id?: string | null;
  opening_qty: number | null;
  opening_time: string | null;
  received_qty: number | null;
  received_time: string | null;
  closing_qty: number | null;
  closing_time: string | null;
  system_sold_qty?: number | null;
  counted_sold_qty?: number | null;
  variance_qty?: number | null;
  system_opening_qty?: number | null;
  system_received_qty?: number | null;
  adjustment_reason?: string | null;
}

export interface CashierCount {
  id: string;
  organization_id: string;
  location_id: string;
  location_name?: string;
  organization_name?: string;
  share_token: string;
  short_code: string;
  cashier_name: string | null;
  cashier_user_id?: string | null;
  status: 'draft' | 'in_progress' | 'completed';
  opening_locked: boolean;
  received_locked: boolean;
  closing_locked: boolean;
  report_email: string | null;
  notes: string | null;
  completed_at: string | null;
  created_at: string;
  session_id?: string | null;
  session_opened_at?: string | null;
  session_closed_at?: string | null;
  session_status?: 'open' | 'paused' | 'closed' | null;
  system_cash_payments?: number | null;
  system_cash_refunds?: number | null;
  system_void_count?: number | null;
  system_refund_count?: number | null;
  system_payment_breakdown?: Record<string, unknown> | null;
  auto_populated?: boolean;
  inventory_synced?: boolean;
  inventory_synced_at?: string | null;
  items: CashierCountItem[];
}

// Hook for authenticated users to manage cashier counts
export function useCashierCounts() {
  const queryClient = useQueryClient();
  const { currentLocation, locationScope } = useLocationStore();
  const locationId = currentLocation?.id || null;

  const countsQuery = useQuery({
    queryKey: ['cashier-counts', locationId, locationScope],
    queryFn: async () => {
      let query = supabase
        .from('cashier_counts')
        .select(`
          *,
          location:locations(name),
          items:cashier_count_items(*)
        `)
        .order('created_at', { ascending: false });

      if (locationScope === 'single' && locationId) {
        query = query.eq('location_id', locationId);
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data || []).map((c: any) => ({
        ...c,
        location_name: c.location?.name,
        items: (c.items || []).sort((a: any, b: any) => a.sort_order - b.sort_order),
      })) as CashierCount[];
    },
    enabled: true,
  });

  const createCount = useMutation({
    mutationFn: async ({
      locationId,
      organizationId,
      reportEmail,
      cashierUserId,
      cashierName,
      items,
      notes,
      autoPopulate,
    }: {
      locationId: string;
      organizationId: string;
      reportEmail?: string;
      cashierUserId?: string;
      cashierName?: string;
      items: string[];
      notes?: string;
      autoPopulate?: boolean;
    }) => {
      const { data, error } = await supabase.rpc('create_cashier_count', {
        p_location_id: locationId,
        p_organization_id: organizationId,
        p_report_email: reportEmail || null,
        p_cashier_user_id: cashierUserId || null,
        p_cashier_name: cashierName || null,
        p_items: items,
        p_notes: notes || null,
        p_auto_populate: autoPopulate || false,
      });

      if (error) throw error;
      return data as CashierCount;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cashier-counts'] });
    },
  });

  const deleteCount = useMutation({
    mutationFn: async (countId: string) => {
      const { error } = await supabase
        .from('cashier_counts')
        .delete()
        .eq('id', countId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cashier-counts'] });
    },
  });

  return {
    counts: countsQuery.data || [],
    isLoading: countsQuery.isLoading,
    error: countsQuery.error,
    createCount,
    deleteCount,
    refetch: countsQuery.refetch,
  };
}

// Hook for public/anonymous access via share token
export function useCashierCountByToken(token: string | undefined) {
  const queryClient = useQueryClient();

  const countQuery = useQuery({
    queryKey: ['cashier-count-public', token],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_cashier_count_by_token', {
        p_token: token,
      });
      if (error) throw error;
      if (!data) throw new Error('Count not found');
      return data as CashierCount;
    },
    enabled: !!token,
  });

  const updateItem = useMutation({
    mutationFn: async ({
      itemId,
      field,
      value,
      adjustmentReason,
    }: {
      itemId: string;
      field: 'opening_qty' | 'received_qty' | 'closing_qty';
      value: number;
      adjustmentReason?: string;
    }) => {
      const { data, error } = await supabase.rpc('update_cashier_count_item_by_token', {
        p_token: token,
        p_item_id: itemId,
        p_field: field,
        p_value: value,
        p_adjustment_reason: adjustmentReason || null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cashier-count-public', token] });
    },
  });

  const refreshReceived = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('refresh_count_received_by_token', {
        p_token: token,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cashier-count-public', token] });
    },
  });

  const lockPhase = useMutation({
    mutationFn: async (phase: 'opening' | 'received' | 'closing') => {
      const { data, error } = await supabase.rpc('lock_cashier_count_phase_by_token', {
        p_token: token,
        p_phase: phase,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cashier-count-public', token] });
    },
  });

  const setCashierName = useMutation({
    mutationFn: async (name: string) => {
      const { data, error } = await supabase.rpc('set_cashier_count_name_by_token', {
        p_token: token,
        p_name: name,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cashier-count-public', token] });
    },
  });

  const addItem = useMutation({
    mutationFn: async (name: string) => {
      const { data, error } = await supabase.rpc('add_cashier_count_item_by_token', {
        p_token: token,
        p_name: name,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cashier-count-public', token] });
    },
  });

  const removeItem = useMutation({
    mutationFn: async (itemId: string) => {
      const { data, error } = await supabase.rpc('remove_cashier_count_item_by_token', {
        p_token: token,
        p_item_id: itemId,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cashier-count-public', token] });
    },
  });

  const completeCount = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('complete_cashier_count_by_token', {
        p_token: token,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cashier-count-public', token] });
    },
  });

  const sendReport = useMutation({
    mutationFn: async (overrideEmail?: string) => {
      const { data, error } = await supabase.functions.invoke('send-cashier-count-report', {
        body: {
          share_token: token,
          ...(overrideEmail ? { override_email: overrideEmail } : {}),
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    retry: 2,
    retryDelay: 1000,
  });

  return {
    count: countQuery.data || null,
    isLoading: countQuery.isLoading,
    error: countQuery.error,
    updateItem,
    refreshReceived,
    lockPhase,
    setCashierName,
    addItem,
    removeItem,
    completeCount,
    sendReport,
    refetch: countQuery.refetch,
  };
}
